import Button from "@/components/atoms/Button";
import Wrapper from "@/components/organisms/Wrapper";
import { GlobalContext } from "@/contexts/SiteDataContext";
import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";
import "./styles.scss";

interface ActionButtonsTypes {
  isEditingForm?: boolean;
  currentStep: number;
  totalSteps: number;
  isReviewPage?: boolean;
  submitApplication?: boolean;
  handleBack?: () => void;
  handleNext?: () => void;
  applicationValues: Record<string, any>;
  pdfId?: string | null;
  isValid: boolean;
}

const ActionButtons = ({
  isEditingForm,
  currentStep,
  totalSteps,
  handleBack,
  handleNext,
}: ActionButtonsTypes) => {
  const { t } = useContext(GlobalContext).context;
  const inlineRef = useRef<HTMLDivElement>(null);
  const [showFixed, setShowFixed] = useState(false);

  const totalStepsWithoutReview = totalSteps - 1;

  useEffect(() => {
    const el = inlineRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const viewportBottom = entry.rootBounds?.bottom ?? window.innerHeight;
        // Only show fixed while inline buttons are still below the viewport
        setShowFixed(entry.boundingClientRect.top >= viewportBottom);
      },
      { threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const renderButtons = (idSuffix = "") =>
    !isEditingForm ? (
      <>
        {currentStep > 0 && (
          <div className="ActionButtons__button">
            <Button
              label={t["Back"]}
              dataQa="back-step"
              testId={`back-step-${currentStep}${idSuffix}`}
              buttonType="button"
              onClick={handleBack}
              tertiary
            />
          </div>
        )}

        {currentStep < totalStepsWithoutReview && (
          <div className="ActionButtons__button ActionButtons__button--right">
            <Button
              label={t["Next Step"]}
              dataQa="next-step"
              testId={`next-step-${currentStep}${idSuffix}`}
              onClick={handleNext}
              primaryAlt
            />
          </div>
        )}

        {currentStep === totalStepsWithoutReview && (
          <div className="ActionButtons__button ActionButtons__button--right">
            <Button
              label={t["Review Application"]}
              buttonId={idSuffix ? undefined : "submit-application"}
              dataQa="complete-application"
              testId={`complete-application-${currentStep}${idSuffix}`}
              buttonSubmit
              primary
            />
          </div>
        )}
      </>
    ) : (
      <div className="ActionButtons__button ActionButtons__button--right">
        <Button
          label={t["Save changes"]}
          dataQa="save-changes"
          buttonId={idSuffix ? undefined : "submit-application"}
          testId={`save-changes-${currentStep}${idSuffix}`}
          buttonSubmit
          primary
        />
      </div>
    );

  const fixedClassNames = classNames("ActionButtons", "ActionButtons--fixedMobile", {
    "ActionButtons--fixedMobileHidden": !showFixed,
  });

  return (
    <>
      <div ref={inlineRef}>
        <Wrapper
          flex
          paddingHorizontal={false}
          classNames="ActionButtons">
          {renderButtons()}
        </Wrapper>
      </div>
      <Wrapper
        flex
        paddingHorizontal={false}
        classNames={fixedClassNames}>
        {renderButtons("-fixed")}
      </Wrapper>
    </>
  );
};

export default ActionButtons;
